import { CUSTOM_ELEMENTS_SCHEMA, ChangeDetectionStrategy, Component, Input, OnInit } from '@angular/core';
import { MaintenanceOrderResponse } from '../../models/maintenance-order-response.model';
import { TableColumn } from '@maersk-global/mds-components-core/mc-table/types';
import { utils } from '../../helper/utils';
import { AuditLog } from '../../models/audit-log.model';
import { APICallStatus } from '../../models/api-call-status.mode';
import { WorkOrderService } from '../../services/work-order.service';
import { ApplicationtTextService } from '../../services/application-text.service';
import { ToastrService } from '../../services/toastr-service';
import { ReplaySubject, takeUntil } from 'rxjs';

@Component({
  selector: 'app-aems-audit-log',
  imports: [],
  templateUrl: './aems-audit-log.component.html',
  styleUrl: './aems-audit-log.component.scss',
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  changeDetection: ChangeDetectionStrategy.Default,
})
export class AemsAuditLogComponent implements OnInit {
  @Input() workOrder!: MaintenanceOrderResponse | null;
  newAuditLogData: AuditLog[] = [];
  mockDataForLoadingAnimation: { id: number }[] = [...Array(30).keys()].map((i) => ({ id: i }));
  firstPage: number | undefined;
  previousPage: number | undefined;
  currentPage: number = 1;
  nextPage: number | undefined;
  lastPage: number | undefined;
  totalItemCount: number | undefined;
  apiVersion: string | undefined;
  pageLimit: number = 100;
  isSystemLog: boolean = false;
  auditlogNewApiCallStatus: APICallStatus = 'NOT_STARTED';

  // Custom styles to wrap only description column
  customTableStyles = `
    td[data-header-id="description"] {
      white-space: normal !important;
      overflow: visible !important;
      word-wrap: break-word !important;
    }
  `;

  auditColumns: Array<TableColumn> = [
    {
      id: 'auditDate',
      label: 'Date',
      width: '10%',
      dataType: {
        type: 'string',
        formatter: (value) => utils.formatUtcTimestamp(value as string),
      },
    },
    {
      id: 'changeUser',
      label: 'User',
      width: '10%',
    },
    {
      id: 'description',
      label: 'Description',
      noWrap: false,
      width: '500px',
    },
  ];

  private destroyed$: ReplaySubject<boolean> = new ReplaySubject(1);

  get workOrderStatusDescription(): string {
    return this.workOrder ? utils.getMaintenanceOrderStatusText(this.workOrder.maintenanceOrderStatusCode).description : '';
  }

  constructor(
    private workOrderService: WorkOrderService,
    private toastrService: ToastrService,
    private appTextService: ApplicationtTextService,
  ) {}

  ngOnInit(): void {
    this.getAuditLogsFromNewAPI();
  }
  showSystemUpdate(): void {
    this.isSystemLog = !this.isSystemLog;
    this.getAuditLogsFromNewAPI();
  }

  getAuditLogsFromNewAPI(): void {
    this.auditlogNewApiCallStatus = 'IN_PROGRESS';
    if (this.workOrder && Number(this.workOrder?.maintenanceOrderNumber) > 0) {
      this.workOrderService
        .getAuditLogsFromNewAPI(
          this.workOrder?.maintenanceOrderNumber as number,
          this.isSystemLog,
          this.currentPage,
          this.pageLimit || 20, // Use `||` to handle `0` as invalid
        )
        .pipe(takeUntil(this.destroyed$))
        .subscribe({
          next: (response) => {
            const headers = response.headers;
            const body = response.body || [];
            this.newAuditLogData = [...body].sort((a, b) => new Date(b.auditDate).getTime() - new Date(a.auditDate).getTime());

            if (headers) {
              this.firstPage = headers.get('First-Page') ? parseInt(headers.get('First-Page')!, 10) : undefined;
              this.previousPage = headers.get('Previous-Page') ? parseInt(headers.get('Previous-Page')!, 10) : undefined;
              this.currentPage = headers.get('Current-Page') ? parseInt(headers.get('Current-Page')!, 10) : this.currentPage;
              this.nextPage = headers.get('Next-Page') ? parseInt(headers.get('Next-Page')!, 10) : undefined;
              this.lastPage = headers.get('Last-Page') ? parseInt(headers.get('Last-Page')!, 10) : undefined;
              this.totalItemCount = headers.get('Total-Item-Count') ? parseInt(headers.get('Total-Item-Count')!, 10) : undefined;
              this.apiVersion = headers.get('API-Version') || undefined;
            }

            this.auditlogNewApiCallStatus = 'COMPLETED_SUCCESFULLY';
          },
          error: (err) => {
            this.auditlogNewApiCallStatus = 'FAILED';
            this.toastrService.showToastr(this.appTextService.auditLogCannotBeFetched, 'error');
            this.newAuditLogData = [];
          },
        });
    }
  }

  onPageLimitChange(event: Event): void {
    let customEvent = event as CustomEvent;
    this.currentPage = 1;
    this.pageLimit = customEvent.detail.value;
    this.getAuditLogsFromNewAPI();
  }

  onPageChange(event: Event): void {
    let customEvent = event as CustomEvent;
    let changedPage = customEvent.detail as number;
    if (changedPage != this.currentPage) {
      this.currentPage = changedPage;
      this.getAuditLogsFromNewAPI();
    }
  }

  getValue(row: AuditLog, key: string): string | number | boolean | Date {
    return row[key as keyof AuditLog] ?? '-';
  }
}
